import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  BarChart3,
  Check,
  CircleCheck,
  CircleDot,
  Clock4,
  Eye,
  MapPin,
  PauseCircle,
  Percent,
  Play,
  Table2,
  UserCheck,
} from "lucide-react";
import AppContext from "../../../../context/AppContext";
import translations from "../../../common/Translations";
import ReactSelector from "../../../common/CommonReactSelector";
import CommonTooltip from "../../../common/CommonTooltip";
import ActionIconButton from "../../../common/ActionIconButton";
import ChartModal from "./ChartModal";
import { fetchGovernorateTaskCounts } from "../../../../service/UserDashboard";
import { fetchFieldAgentMap } from "../../../../service/Task";
import { isRTL } from "../../../../utils/rtl";

/* Task counts per governorate, split by status, for the selected workflow and
   (optionally) a single field agent.

   Form: horizontal stacked bar. Each governorate is one bar, so the stack reads
   as that governorate's status mix and the bar length as its total. Horizontal
   because governorate names ("South Sharqiyah") don't fit under vertical bars.

   Colour: the six statuses are categorical identity, not severity, so they take
   the first six categorical slots in fixed order -- never cycled, and never the
   reserved status palette. Both columns below were checked with the dataviz
   validator and pass every gate on the adjacent pairlist (the correct pairlist
   for stacks and bars) in light and dark:
     light  worst adjacent CVD dE 9.1, normal-vision 19.6
     dark   worst adjacent CVD dE 8.4, normal-vision 19.3
   Three light steps sit below 3:1 contrast on a light surface, which triggers
   the relief rule -- hence the always-present legend, the per-bar totals and the
   table view toggle, so identity and value are never carried by colour alone.

   Each status also carries an icon. The legend is rendered as plain HTML rather
   than by Apex so the icon sits beside the colour chip: identity then has a
   shape as well as a hue, which is what makes it survive colourblindness,
   greyscale printing and forced-colors mode. */
/* labelKey is deliberately status-prefixed rather than reusing the bare
   `assigned` / `inProgress` / `completed` keys that already exist in the
   dictionary: those are older entries whose Arabic drifted from their English
   ("assigned" reads as "مهام" -- tasks), and they're shared with other screens,
   so correcting them here would move text the whole app depends on. */
const STATUS_SERIES = [
  { key: "assigned", labelKey: "statusAssigned", fallback: "Assigned", light: "#2a78d6", dark: "#3987e5", Icon: UserCheck },
  { key: "inProgress", labelKey: "statusInProgress", fallback: "In Progress", light: "#eb6834", dark: "#d95926", Icon: Play },
  { key: "onHold", labelKey: "statusOnHold", fallback: "On Hold", light: "#1baf7a", dark: "#199e70", Icon: PauseCircle },
  { key: "pending", labelKey: "statusPending", fallback: "Pending", light: "#eda100", dark: "#c98500", Icon: Clock4 },
  { key: "completed", labelKey: "statusCompleted", fallback: "Completed", light: "#e87ba4", dark: "#d55181", Icon: CircleCheck },
  { key: "finished", labelKey: "statusFinished", fallback: "Finished", light: "#008300", dark: "#008300", Icon: Check },
];

const LIGHT_SURFACE = "#ffffff";
const DARK_SURFACE = "#1f2937";

/* Empty states carry an illustration rather than a bare line of grey text, so a
   card with nothing in it still reads as deliberate instead of broken.

   It is drawn from the card's own vocabulary -- stacked bars behind a map pin --
   so it reads as "this is the governorate chart, waiting" rather than generic
   clip art. `currentColor` throughout, so one drawing works on light and dark
   and inherits whatever muted ink the parent sets. The mark is decorative, hence
   aria-hidden: the adjacent text is the accessible message. */
const GovernorateArt = ({ variant = "waiting" }) => (
  <svg
    viewBox="0 0 96 72"
    className="h-[74px] w-[98px] shrink-0"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    {/* baseline */}
    <path d="M14 58h68" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
    {/* three bars of decreasing length, echoing the real chart */}
    <rect x="14" y="46" width="40" height="7" rx="3.5" strokeWidth="2" opacity="0.5" />
    <rect x="14" y="34" width="27" height="7" rx="3.5" strokeWidth="2" opacity="0.35" />
    <rect x="14" y="22" width="15" height="7" rx="3.5" strokeWidth="2" opacity="0.25" />
    {variant === "empty" ? (
      /* nothing found: an empty outlined pin */
      <>
        <path
          d="M70 12c-6.1 0-11 4.9-11 11 0 7.7 11 18 11 18s11-10.3 11-18c0-6.1-4.9-11-11-11z"
          strokeWidth="2"
          strokeLinejoin="round"
          opacity="0.55"
        />
        <path d="M65.5 23h9" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      </>
    ) : (
      /* waiting for input: pin with a search-style ring */
      <>
        <path
          d="M70 12c-6.1 0-11 4.9-11 11 0 7.7 11 18 11 18s11-10.3 11-18c0-6.1-4.9-11-11-11z"
          strokeWidth="2"
          strokeLinejoin="round"
          opacity="0.55"
        />
        <circle cx="70" cy="23" r="3.5" strokeWidth="2" opacity="0.7" />
      </>
    )}
  </svg>
);

const toCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

/* Apex draws y-axis labels as SVG <text> and ellipsizes them at maxWidth, with
   no affordance for reading what got cut -- "South Sharqi..." is a dead end.
   CommonTooltip can't wrap an SVG node, so instead we measure where Apex put
   each label and lay a transparent HTML box over it; that box is what carries
   the tooltip. Positions are relative to the plot wrapper, so they survive the
   page scrolling.

   Labels are matched to rows by position, but the drawn text is checked against
   the name as a prefix first: if Apex ever changes render order, the fallback
   search keeps the tooltip honest rather than silently mislabelling a row. */
const readAxisLabelBoxes = (chartContext, host, names) => {
  const root = chartContext?.el;
  if (!root || !host) return [];
  const hostRect = host.getBoundingClientRect();

  return Array.from(root.querySelectorAll(".apexcharts-yaxis-label"))
    .map((node, index) => {
      const drawn = (node.textContent || "").replace(/[\s.…]+$/, "");
      const byIndex = names[index];
      const name =
        byIndex && byIndex.startsWith(drawn)
          ? byIndex
          : names.find((candidate) => candidate.startsWith(drawn)) || byIndex;
      if (!name) return null;

      const rect = node.getBoundingClientRect();
      return {
        name,
        top: rect.top - hostRect.top,
        left: rect.left - hostRect.left,
        width: rect.width,
        height: rect.height,
      };
    })
    .filter(Boolean);
};

// Cheap structural compare, so re-measuring to the same place doesn't set state
// and bounce us round the render -> Apex `updated` -> re-measure loop.
const sameBoxes = (a, b) =>
  a.length === b.length &&
  a.every((box, index) => {
    const other = b[index];
    return (
      other &&
      box.name === other.name &&
      Math.round(box.top) === Math.round(other.top) &&
      Math.round(box.left) === Math.round(other.left) &&
      Math.round(box.width) === Math.round(other.width)
    );
  });

const GovernorateTaskChart = ({ selectedWorkflowId }) => {
  const { theme, langMode } = useContext(AppContext);
  const isDark = theme === "dark" || theme === "high-contrast";
  /* Apex doesn't inherit the page's `dir`, so the plot has to be flipped
     explicitly: in Arabic and Urdu the bars must grow right-to-left from an axis
     on the right, or the one element on the card still reading left-to-right is
     the chart itself. */
  const isRtl = isRTL(langMode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [agentOptions, setAgentOptions] = useState([]);
  /* One view at a time, picked from the eye-icon modal every other dashboard
     card uses. Held as a single value rather than two booleans so "table" and
     "share" can't both be on -- and so the modal has one thing to highlight.
       bar   - stacked counts, bar length is the task count
       share - 100% stacked; see below
       table - exact numbers for all six statuses

     Share exists because the counts are heavily skewed: one governorate at 561
     against several under 30 means the small ones are a few pixels long and
     their status mix is unreadable, which is what breaks first on a phone. Share
     gives every governorate the full bar width; the real totals stay on the bar
     ends and in the tooltip, so nothing is traded away for it. */
  const [viewMode, setViewMode] = useState("bar");
  const [showViewModal, setShowViewModal] = useState(false);
  // Hover targets laid over the chart's y-axis labels; see readAxisLabelBoxes.
  const [axisLabelBoxes, setAxisLabelBoxes] = useState([]);
  const plotRef = useRef(null);
  const showTable = viewMode === "table";
  const percentMode = viewMode === "share";
  // Discards out-of-order responses: switching field agent quickly on a slow
  // query returns them in whatever order the backend finishes.
  const requestIdRef = useRef(0);

  /* Layout follows the CARD's width, not the viewport's. The card spans three
     grid columns on xl, two on md and one below that, so a viewport media query
     would call a 380px-wide card "desktop" on a wide screen and lay it out for
     space it doesn't have. ResizeObserver measures what the chart actually gets.

     Starts at null rather than 0 so the first paint isn't rendered as "narrow"
     and then reflowed -- until the observer reports, treat it as roomy. */
  const cardRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(null);

  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (typeof width === "number") setCardWidth(width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Phone-width card: y-axis names have to give up room to the bars themselves.
  const isNarrow = cardWidth !== null && cardWidth < 520;
  // Between phone and full desktop -- names get less room but stay readable.
  const isCompact = cardWidth !== null && cardWidth < 760;

  const label = (key, fallback) => translations[langMode]?.[key] ?? fallback;

  // Field agents are per workflow, so the list reloads with it and any agent
  // selected for the previous workflow is dropped.
  useEffect(() => {
    let cancelled = false;
    setAssigneeId("");

    if (!selectedWorkflowId) {
      setAgentOptions([]);
      return undefined;
    }

    fetchFieldAgentMap(selectedWorkflowId)
      .then((response) => {
        if (cancelled) return;
        const agents = Array.isArray(response?.body?.value) ? response.body.value : [];
        setAgentOptions(
          agents
            .filter((agent) => agent?.userId != null)
            .map((agent) => ({
              value: String(agent.userId),
              label: agent.name || agent.email || `#${agent.userId}`,
            }))
        );
      })
      .catch((error) => {
        console.error("Failed to load field agents for governorate chart:", error);
        if (!cancelled) setAgentOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedWorkflowId]);

  /* A field agent is required, not optional: nothing is fetched until one is
     picked. Bumping the request id on the way out also invalidates any response
     still in flight from a previous selection, so clearing the agent can't be
     overwritten by a late arrival. */
  useEffect(() => {
    if (!selectedWorkflowId || !assigneeId) {
      requestIdRef.current += 1;
      setRows([]);
      setLoading(false);
      setLoadError(false);
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError(false);

    fetchGovernorateTaskCounts({
      workflowId: selectedWorkflowId,
      assigneeId,
      signal: controller.signal,
    })
      .then((value) => {
        if (requestId !== requestIdRef.current) return;
        setRows(value);
        setLoading(false);
      })
      .catch((error) => {
        if (
          axios.isCancel(error) ||
          error?.name === "CanceledError" ||
          error?.name === "AbortError"
        ) {
          return;
        }
        if (requestId !== requestIdRef.current) return;
        setRows([]);
        setLoadError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [selectedWorkflowId, assigneeId]);

  /* Governorates with no tasks at all carry no information here and just cost
     vertical space (the API returns every governorate, several of them empty),
     so they're dropped. Busiest first, so the bars a reader cares about are at
     the top rather than in API order. */
  const visibleRows = useMemo(() => {
    const withTotals = (rows || []).map((row) => {
      const counts = STATUS_SERIES.reduce((acc, series) => {
        acc[series.key] = toCount(row?.[series.key]);
        return acc;
      }, {});
      const summed = STATUS_SERIES.reduce((sum, series) => sum + counts[series.key], 0);
      return {
        governorate: String(row?.governorate ?? "").trim() || label("notSpecified", "Not specified"),
        counts,
        // Prefer the API's own total; fall back to the sum if it's absent.
        total: toCount(row?.total) || summed,
      };
    });

    return withTotals.filter((row) => row.total > 0).sort((a, b) => b.total - a.total);
  }, [rows, langMode]);

  /* Named, not just counted: "5 governorate(s) with no task" tells you the
     coverage gap exists but not where, which is the actionable part. */
  const emptyGovernorates = useMemo(() => {
    const named = visibleRows.map((row) => row.governorate);
    return (rows || [])
      .map((row) => String(row?.governorate ?? "").trim())
      .filter((name) => name && !named.includes(name))
      .sort((a, b) => a.localeCompare(b));
  }, [rows, visibleRows]);

  const hiddenEmptyCount = emptyGovernorates.length;

  /* Headline figures across every governorate. The three non-total tiles are a
     partition of the six statuses, so done + open + blocked always equals total
     and the percentages sum to 100 -- no task is counted twice or missed. */
  const summary = useMemo(() => {
    const sum = (key) => visibleRows.reduce((acc, row) => acc + row.counts[key], 0);
    const done = sum("completed") + sum("finished");
    const open = sum("assigned") + sum("inProgress");
    const blocked = sum("onHold") + sum("pending");
    const total = done + open + blocked;
    /* A non-zero count must never read "0%" -- 7 blocked tasks rounding to 0 is
       indistinguishable from none at all, which is exactly the case someone
       looks at this tile to find. */
    const share = (part) => {
      if (total <= 0 || part <= 0) return "0%";
      const pct = Math.round((part / total) * 100);
      return pct < 1 ? "<1%" : `${pct}%`;
    };

    return { total, done, open, blocked, donePct: share(done), openPct: share(open), blockedPct: share(blocked) };
  }, [visibleRows]);

  /* Statuses that are zero in every governorate are dropped from the plot and
     the legend: they draw nothing, so six legend swatches for one populated
     status is noise. Nothing is lost -- the table view still lists all six.

     Each survivor keeps the colour of its OWN slot rather than being re-indexed,
     so changing field agent (and with it which statuses appear) never repaints
     the ones that stayed. Colour follows the status, not its position. */
  const activeStatuses = useMemo(() => {
    const present = STATUS_SERIES.filter((status) =>
      visibleRows.some((row) => row.counts[status.key] > 0)
    );
    // Never hand Apex an empty series list; fall back to the full set.
    return present.length > 0 ? present : STATUS_SERIES;
  }, [visibleRows]);

  /* Statuses the viewer has clicked off in the legend. They stay listed (dimmed)
     so the choice is reversible and visible, but leave the plot. */
  const [mutedKeys, setMutedKeys] = useState([]);
  const toggleStatus = (key) =>
    setMutedKeys((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );

  /* No "fall back to everything" guard here. It used to return activeStatuses
     when the kept list was empty, which meant muting the only populated status
     silently re-showed it -- the toggle looked broken. An empty plot is a valid
     state; it gets its own message below, with the legend still on screen so the
     status can be switched back on. */
  const plottedStatuses = useMemo(
    () => activeStatuses.filter((status) => !mutedKeys.includes(status.key)),
    [activeStatuses, mutedKeys]
  );

  const series = useMemo(
    () =>
      plottedStatuses.map((status) => ({
        name: label(status.labelKey, status.fallback),
        data: visibleRows.map((row) => row.counts[status.key]),
      })),
    [plottedStatuses, visibleRows, langMode]
  );

  /* Held in a ref and called indirectly from the Apex event handlers, so the
     handlers themselves never change identity -- putting this in chartOptions'
     dependency list would rebuild the options on every render, which makes Apex
     redraw, which fires the event again. */
  const measureAxisLabels = (chartContext) => {
    const next = readAxisLabelBoxes(
      chartContext,
      plotRef.current,
      visibleRows.map((row) => row.governorate)
    );
    setAxisLabelBoxes((current) => (sameBoxes(current, next) ? current : next));
  };
  const measureRef = useRef(measureAxisLabels);
  measureRef.current = measureAxisLabels;

  const chartOptions = useMemo(() => {
    const surface = isDark ? DARK_SURFACE : LIGHT_SURFACE;
    const inkPrimary = isDark ? "#e5e7eb" : "#334155";
    const inkMuted = isDark ? "#9ca3af" : "#64748b";
    const gridLine = isDark ? "#374151" : "#e2e8f0";
    const axisFontSize = isNarrow ? "10px" : "11px";
    /* Apex ellipsizes a y-axis label that exceeds maxWidth, so a long
       governorate name clips instead of stealing the plot area or overlapping
       the bars. The full name is never lost -- the tooltip's x formatter names
       the governorate, and the table view spells it out in full. */
    const nameMaxWidth = isNarrow ? 78 : isCompact ? 140 : 200;
    /* Room for the total printed past the end of each bar: its offset, roughly a
       digit's width per character, and a little slack. Measured off the largest
       total so a four-figure count doesn't clip where a two-figure one fit. */
    const widestTotal = visibleRows.reduce((max, row) => Math.max(max, row.total), 0);
    const totalLabelGutterPx = 14 + String(widestTotal).length * 7;

    return {
      chart: {
        type: "bar",
        stacked: true,
        stackType: percentMode ? "100%" : "normal",
        toolbar: { show: false },
        fontFamily: "inherit",
        background: "transparent",
        animations: { enabled: false },
        events: {
          mounted: (chartContext) => measureRef.current(chartContext),
          updated: (chartContext) => measureRef.current(chartContext),
        },
      },
      colors: plottedStatuses.map((status) => (isDark ? status.dark : status.light)),
      plotOptions: {
        bar: {
          horizontal: true,
          // Thin marks; 4px rounded data-end on the outer segment only, so the
          // stack still reads as one bar anchored to the baseline.
          // Thin marks. The band is a fixed BAR_ROW_HEIGHT_PX tall, so this
          // percentage yields a consistent bar weight rather than a slab.
          barHeight: "58%",
          /* A 4px radius is most of a segment that's only a few pixels long, so
             the smallest governorates rendered as faint smudges on a narrow plot.
             Halve it there -- the bar still reads as rounded, but the geometry is
             spent on the data rather than on the corner. */
          borderRadius: isNarrow ? 2 : 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: {
            // One selective label per bar (its total) rather than a number on
            // every segment. Formatted from our own rows so it stays the real
            // task count in percent mode, where every bar is the same length and
            // Apex's own total would read "100".
            total: {
              enabled: true,
              // Sits just past the end of the bar -- which is the left end once
              // the axis is reversed, so the offset has to flip with it.
              offsetX: isRtl ? -6 : 6,
              formatter: (value, opts) => {
                const row = visibleRows[opts?.dataPointIndex];
                return `${row ? row.total : toCount(value)}`;
              },
              style: { fontSize: axisFontSize, fontWeight: 600, color: inkPrimary },
            },
          },
        },
      },
      /* Surface-coloured gap between stacked segments. Narrowed on a small plot:
         at 2px the separators were wider than the segments they divided on the
         shortest bars, so a 20-task governorate disappeared into its own gaps. */
      stroke: { show: true, width: isNarrow ? 1 : 2, colors: [surface] },
      dataLabels: { enabled: false },
      // Rendered as plain HTML below the plot instead (see statusLegend), so each
      // status can carry an icon alongside its colour chip.
      legend: { show: false },
      xaxis: {
        categories: visibleRows.map((row) => row.governorate),
        labels: {
          style: { colors: inkMuted, fontSize: axisFontSize },
          // Share mode's axis runs 0-100, so it has to be marked as a percentage
          // or it reads as a task count that happens to stop at 100.
          formatter: (value) =>
            percentMode ? `${Math.round(toCount(value))}%` : `${Math.round(toCount(value))}`,
          /* A narrow card can't fit ten tick numbers along the bottom without
             them colliding, so ask for fewer and let Apex pick the stops. */
          ...(isNarrow ? { rotate: 0, hideOverlappingLabels: true } : {}),
        },
        tickAmount: isNarrow ? 3 : undefined,
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: { text: undefined },
      },
      yaxis: {
        /* For a horizontal bar chart this reverses the value direction, so bars
           run right-to-left; Apex then sets `opposite` itself, which moves the
           governorate names to the right-hand side. Both halves of the RTL flip
           come from this one flag. */
        reversed: isRtl,
        labels: {
          maxWidth: nameMaxWidth,
          style: { colors: inkPrimary, fontSize: axisFontSize },
        },
      },
      grid: {
        borderColor: gridLine,
        strokeDashArray: 3,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        /* Right padding is sized from the widest total the labels have to fit,
           not a guess. In share mode EVERY bar ends at the plot edge, so a fixed
           16px clipped all ten totals ("561" showing as "5"); in count mode the
           longest bar has the same problem. Same formula covers both, since the
           longest bar is also the largest number. */
        padding: {
          left: isRtl ? totalLabelGutterPx : 4,
          right: isRtl ? 4 : totalLabelGutterPx,
          top: 0,
          bottom: 0,
        },
      },
      // Per-mark hover, so hovering a segment names that status and value. The
      // raw count is always shown -- percent mode changes the geometry, not what
      // the number means -- with its share of the bar alongside it.
      tooltip: {
        shared: false,
        intersect: true,
        theme: isDark ? "dark" : "light",
        // Apex was rendering an empty title strip above the value, because for a
        // horizontal bar the category sits on the y axis and its default x-title
        // resolves to nothing. Name the governorate explicitly so the tooltip
        // says which bar it belongs to.
        x: {
          show: true,
          formatter: (value, opts) =>
            visibleRows[opts?.dataPointIndex]?.governorate ?? value ?? "",
        },
        y: {
          formatter: (value, opts) => {
            const count = toCount(value);
            const barTotal = toCount(
              opts?.w?.globals?.stackedSeriesTotals?.[opts?.dataPointIndex]
            );
            if (barTotal <= 0) return `${count}`;
            return `${count} (${Math.round((count / barTotal) * 100)}%)`;
          },
        },
      },
      states: { active: { filter: { type: "none" } } },
      noData: { text: "" },
    };
  }, [isDark, visibleRows, langMode, plottedStatuses, isNarrow, isCompact, percentMode, isRtl]);

  /* Bar thickness is driven by a fixed pixel budget per governorate rather than
     a percentage of whatever height is left over. Stretching to fill made four
     governorates render as slabs while ten were cramped; a constant row height
     keeps a bar the same weight regardless of how many there are, and the card
     grows downward when the list is long instead of squeezing. (It grows rather
     than scrolls -- see the body below for why the plot can't be clipped.) */
  const BAR_ROW_HEIGHT_PX = 34;
  // x-axis labels only now -- the legend lives outside the plot as HTML.
  const PLOT_CHROME_PX = 56;
  const chartHeightPx = Math.max(
    180,
    visibleRows.length * BAR_ROW_HEIGHT_PX + PLOT_CHROME_PX
  );

  /* The three views, as the eye-icon picker presents them. Icons keep the h-9
     w-9 + single-hue treatment the existing chart picker uses, so this modal
     looks like the one every other card opens. */
  const viewOptions = [
    {
      key: "bar",
      value: "bar",
      name: label("barChartView", "Bar chart"),
      description: label("barChartViewHint", "Bar length is the task count"),
      icon: <BarChart3 className="h-9 w-9 text-blue-600 transition-colors duration-200" />,
    },
    {
      key: "share",
      value: "share",
      name: label("shareView", "Share"),
      description: label("shareViewHint", "Every bar full width, to compare the status mix"),
      icon: <Percent className="h-9 w-9 text-amber-500 transition-colors duration-200" />,
    },
    {
      key: "table",
      value: "table",
      name: label("tableView", "Table"),
      description: label("tableViewHint", "Exact numbers for all six statuses"),
      icon: <Table2 className="h-9 w-9 text-emerald-600 transition-colors duration-200" />,
    },
  ];

  /* Title and controls sit on one line while they fit and stack when they don't.
     The controls row goes full width once stacked -- a 150px picker marooned on
     the left of an otherwise empty line looks like a mistake, and the agent name
     it has to show ("Pruthvi Eemad") needs whatever width is going. */
  const headerRow = (
    <div className="mb-3 flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <MapPin size={16} className="shrink-0 text-[#236DB4]" />
        <h3 className="truncate text-sm font-semibold text-slate-700 dark:text-gray-200">
          {label("tasksByGovernorate", "Tasks by Governorate")}
        </h3>
      </div>
      <div className="flex w-full items-center gap-2 sm:w-auto">
        <div className="min-w-0 flex-1 sm:w-[170px] sm:flex-none">
          <ReactSelector
            value={agentOptions.find((option) => option.value === assigneeId) || null}
            options={agentOptions}
            isClearable
            isDisabled={!selectedWorkflowId || agentOptions.length === 0}
            placeholder={label("selectFieldAgent", "Select field agent")}
            onChange={(option) => setAssigneeId(option?.value ?? "")}
            height="32px"
            fontSize="12px"
            menuPortalTarget={document.body}
          />
        </div>

        {/* Same affordance as every other dashboard card: one eye icon opening
            the picker, rather than this card inventing its own inline toggles.
            It also keeps the header to a single control on a phone, where two
            32px buttons plus the agent picker had nothing left to give. */}
        <ActionIconButton
          tone="gray"
          className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          onClick={() => setShowViewModal(true)}
          ariaLabel={label("selectView", "Select view")}
          tooltipText={label("selectView", "Select view")}
          tooltipPosition="top"
        >
          <Eye size={18} />
        </ActionIconButton>
      </div>
    </div>
  );

  /* Headline figures. Plain stat tiles -- no plot, so no hover layer -- with the
     value in ink, never a series colour. */
  const summaryRow = (
    /* Two-up on a narrow card, four-up when there's room. Driven by the measured
       card width rather than a `sm:` breakpoint, for the same reason the chart is
       -- this card is one column wide on md, so the viewport says nothing useful
       about how much space these tiles actually have. */
    <div
      className={`mb-3 grid shrink-0 gap-2 ${isNarrow ? "grid-cols-2" : "grid-cols-4"}`}
    >
      {[
        /* These three are buckets, not statuses, so they get their own names.
           Labelling the assigned+inProgress bucket "In Progress" made the tile
           contradict the bars, which showed those tasks as Assigned. `hint` spells
           out the contents in the tooltip, since "Done" alone doesn't say whether
           Finished is included. */
        {
          text: label("total", "Total"),
          value: summary.total,
          share: null,
          Icon: CircleDot,
          hint: label("totalAcrossGovernorates", "All tasks across governorates that have tasks"),
        },
        { text: label("doneLabel", "Done"), value: summary.done, share: summary.donePct, Icon: CircleCheck, hint: label("completedPlusFinished", "Completed + Finished") },
        { text: label("openLabel", "Open"), value: summary.open, share: summary.openPct, Icon: Play, hint: label("assignedPlusInProgress", "Assigned + In Progress") },
        { text: label("blockedLabel", "Blocked"), value: summary.blocked, share: summary.blockedPct, Icon: PauseCircle, hint: label("onHoldPlusPending", "On Hold + Pending") },
      ].map((tile) => (
        <CommonTooltip
          key={tile.text}
          text={tile.text}
          description={tile.hint}
          position="bottom"
          disableDefaultDescription
          /* CommonTooltip's trigger is an inline-flex span with cursor-pointer.
             These tiles aren't clickable, and as grid children they need to fill
             their cell -- cn() is tailwind-merge, so these win. */
          className="w-full cursor-default"
        >
          {/* text-start, not text-left: in Arabic and Urdu the label and its
              count have to sit against the right edge of the tile like the rest
              of the RTL layout, and a physical `text-left` would pin them to the
              wrong side regardless of direction. */}
          <div className="w-full rounded-xl border border-slate-100 bg-slate-50/70 px-2.5 py-1.5 text-start">
            <p className="flex items-center gap-1 truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <tile.Icon size={11} className="shrink-0" />
              {tile.text}
            </p>
            <p className="text-base font-semibold leading-tight tabular-nums text-slate-700">
              {tile.value}
              {tile.share !== null && (
                <span className="ms-1 text-[11px] font-medium text-slate-400">{tile.share}</span>
              )}
            </p>
          </div>
        </CommonTooltip>
      ))}
    </div>
  );

  /* Legend as HTML rather than Apex's own, so identity is carried by an icon and
     a colour chip together -- the shape is what survives colourblindness and
     greyscale printing, and it's the relief the light palette's three low-contrast
     steps require. Clicking an entry mutes that status, same affordance Apex's
     legend gave us. */
  const statusLegend = (
    <div className="mt-1 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5">
      {activeStatuses.map((status) => {
        const { Icon } = status;
        const isMuted = mutedKeys.includes(status.key);
        const swatch = isDark ? status.dark : status.light;
        return (
          <button
            key={status.key}
            type="button"
            onClick={() => toggleStatus(status.key)}
            aria-pressed={!isMuted}
            className={`inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] transition-opacity hover:bg-slate-50 ${
              isMuted ? "opacity-40" : "opacity-100"
            }`}
          >
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: swatch }}
            />
            <Icon size={12} className="shrink-0 text-slate-400" />
            <span className={`text-slate-500 ${isMuted ? "line-through" : ""}`}>
              {label(status.labelKey, status.fallback)}
            </span>
          </button>
        );
      })}
    </div>
  );

  const centred = (message, { art = "waiting", hint } = {}) => (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-slate-400">
      {art && <GovernorateArt variant={art} />}
      <p className="text-xs font-medium text-slate-500">{message}</p>
      {hint && <p className="max-w-[280px] text-[11px] leading-snug text-slate-400">{hint}</p>}
    </div>
  );

  const plainCentred = (message) => (
    <div className="flex flex-1 items-center justify-center px-2 text-center text-xs text-slate-400">
      {message}
    </div>
  );

  let body;
  if (!selectedWorkflowId) {
    body = centred(label("selectWorkflowToSeeData", "Select a workflow to see data."), {
      hint: label("workflowPickerHint", "Choose a workflow from the picker at the top of the page."),
    });
  } else if (agentOptions.length === 0) {
    body = centred(label("noFieldAgentsForWorkflow", "No field agents on this workflow."), {
      art: "empty",
      hint: label("noFieldAgentsHint", "Assign a field agent to this workflow to see their coverage."),
    });
  } else if (!assigneeId) {
    body = centred(label("selectFieldAgentToSeeData", "Pick a field agent"), {
      hint: label(
        "selectFieldAgentHint",
        "Their tasks will be broken down by governorate and status."
      ),
    });
  } else if (loading) {
    // No illustration while loading: it would flash in and straight back out.
    // Not the shared `loading` key -- that one reads "Loading more worklog...".
    body = plainCentred(label("loadingData", "Loading..."));
  } else if (loadError) {
    body = centred(label("somethingWentWrong", "Could not load governorate data."), {
      art: "empty",
      hint: label("tryAgainHint", "Try selecting the field agent again."),
    });
  } else if (visibleRows.length === 0) {
    body = centred(label("noTasksForAgent", "No tasks for this field agent"), {
      art: "empty",
      hint: label("noTasksForAgentHint", "Nothing has been assigned to them in this workflow yet."),
    });
  } else if (showTable) {
    // The table view is the relief for the three light-mode steps that fall
    // below 3:1 contrast, and doubles as the accessible read of the chart.
    body = (
      /* Capped height so a long governorate list scrolls inside the card instead
         of stretching it down the page. The thead is sticky, so the status
         columns stay readable while scrolling. Safe to clip here, unlike the
         chart: a table has no tooltip to cut off. */
      /* Scrolls on BOTH axes. Vertically it's capped so a long governorate list
         stays inside the card instead of stretching it down the page; horizontally
         the table keeps a min width so eight columns stay legible on a phone and
         the reader pans to them rather than having them crushed to two characters
         each. Safe to clip here, unlike the chart: a table has no tooltip to cut
         off. */
      <div className="min-h-0 max-h-[300px] flex-1 overflow-auto sm:max-h-[360px]">
        {/* Each status column is headed by its own colour chip and icon, so a
            column can be traced back to the segment it represents in the chart.
            The numbers themselves stay in ink -- colouring the digits would make
            the value depend on hue, and three of the light steps don't have
            enough contrast to be read as text. */}
        <table className="w-full min-w-[620px] border-collapse text-start text-[11px]">
          {/* z-20 over the frozen first column's z-10, so the corner cell wins
              where the two sticky axes overlap. */}
          <thead className="sticky top-0 z-20 bg-white dark:bg-gray-800">
            <tr className="text-slate-500">
              <th
                scope="col"
                className="sticky start-0 z-30 border-e border-slate-100 bg-white px-3 py-2.5 font-semibold dark:border-gray-700 dark:bg-gray-800"
              >
                {label("governorate", "Governorate")}
              </th>
              {STATUS_SERIES.map((status) => {
                const { Icon } = status;
                return (
                  <th
                    key={status.key}
                    scope="col"
                    className="whitespace-nowrap px-3 py-2.5 text-end font-semibold"
                    style={{ borderBottom: `2px solid ${isDark ? status.dark : status.light}` }}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Icon size={11} className="shrink-0 text-slate-400" />
                      {label(status.labelKey, status.fallback)}
                    </span>
                  </th>
                );
              })}
              <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-end font-semibold">
                {label("total", "Total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.governorate}
                className="group border-t border-slate-100 hover:bg-slate-50/70 dark:border-gray-700"
              >
                {/* Frozen while the status columns scroll under it: on a phone the
                    numbers are only meaningful if you can still see whose row they
                    belong to. Needs its own opaque background for that, so the row
                    hover is re-applied here via the row's `group`. */}
                <th
                  scope="row"
                  className="sticky start-0 z-10 border-e border-slate-100 bg-white px-3 py-2.5 font-normal text-slate-700 group-hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:group-hover:bg-gray-700/60"
                >
                  {/* truncate needs a block box with a width bound -- a bare
                      class on the <th> is ignored under table-layout: auto, so a
                      long name would stretch the column instead of clipping.
                      The tooltip is what makes the clipped part recoverable. */}
                  <CommonTooltip
                    text={row.governorate}
                    position="top"
                    disableDefaultDescription
                    className="max-w-full cursor-default"
                  >
                    <span className="block max-w-[130px] truncate sm:max-w-[200px]">
                      {row.governorate}
                    </span>
                  </CommonTooltip>
                </th>
                {STATUS_SERIES.map((status) => {
                  const count = row.counts[status.key];
                  const tint = isDark ? status.dark : status.light;
                  return (
                    <td key={status.key} className="px-3 py-2.5 text-end tabular-nums">
                      {count > 0 ? (
                        /* A faint wash of the status colour, so the shape of the
                           data is visible while scanning. Zeroes stay unpainted
                           so the populated cells are what stands out. */
                        <span
                          className="inline-block min-w-[26px] rounded px-1.5 py-0.5 font-medium text-slate-700 dark:text-gray-100"
                          style={{ backgroundColor: `${tint}1f` }}
                        >
                          {count}
                        </span>
                      ) : (
                        <span className="text-slate-300 dark:text-gray-600">0</span>
                      )}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5 text-end font-semibold tabular-nums text-slate-700 dark:text-gray-200">
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else if (plottedStatuses.length === 0) {
    body = plainCentred(
      label("allStatusesHidden", "All statuses hidden - click one below to show it again.")
    );
  } else {
    body = (
      /* Deliberately NOT overflow-auto. A scroll container is a clipping context,
         and Apex renders its tooltip inside the chart DOM -- so hovering a bar
         near the top had the tooltip sliced off at the container edge. The card
         is its own full-width row and sizes to content, so it can simply grow. */
      <div ref={plotRef} className="relative w-full flex-1">
        {/* One transparent box per y-axis label, carrying the full governorate
            name in the app's own tooltip. Sits over the label column only, so it
            never intercepts a hover meant for a bar. */}
        {axisLabelBoxes.map((box) => (
          <div
            key={box.name}
            className="absolute z-10"
            style={{ top: box.top, left: box.left, width: box.width, height: box.height }}
          >
            <CommonTooltip
              text={box.name}
              position="top"
              disableDefaultDescription
              className="h-full w-full cursor-default"
            >
              <span className="block h-full w-full" aria-hidden="true" />
            </CommonTooltip>
          </div>
        ))}
        <ReactApexChart
          /* Remount when the set of plotted statuses changes. react-apexcharts
             patches an existing chart via updateSeries, which does not reliably
             drop a series when the array gets shorter -- a muted status could
             linger on screen. Keying on the set forces a clean render. */
          /* Direction is in the key too: flipping `reversed` moves the axis to
             the other side, which Apex's in-place update path doesn't fully
             redraw -- a language switch would leave the labels stranded. */
          key={`${plottedStatuses.map((status) => status.key).join("|")}-${
            percentMode ? "pct" : "count"
          }-${isRtl ? "rtl" : "ltr"}`}
          options={chartOptions}
          series={series}
          type="bar"
          width="100%"
          height={chartHeightPx}
        />
      </div>
    );
  }

  const hasData = Boolean(assigneeId) && !loading && !loadError && visibleRows.length > 0;

  return (
    <div ref={cardRef} className="flex h-full w-full min-w-0 flex-col">
      <ChartModal
        showModal={showViewModal}
        setShowModal={setShowViewModal}
        options={viewOptions}
        value={viewMode}
        onSelect={setViewMode}
        title={label("selectView", "Select view")}
      />
      {headerRow}
      {hasData && summaryRow}
      {body}
      {hasData && !showTable && statusLegend}
      {/* Governorates the API returned with no tasks at all can't be drawn as
          bars (zero length), so they're named here instead. The names are the
          useful part -- knowing *which* areas this agent has nothing in is the
          coverage gap. Shown in table view too. */}
      {hasData && hiddenEmptyCount > 0 && (
        <div className="mt-1.5 shrink-0 border-t border-slate-100 pt-1.5">
          <p className="text-[10px] leading-snug text-slate-400">
            <span className="font-semibold">
              {`${hiddenEmptyCount} ${
                hiddenEmptyCount === 1
                  ? label("governorateWithNoTask", "governorate with no task")
                  : label("governoratesWithNoTask", "governorate(s) with no task")
              }:`}
            </span>{" "}
            {emptyGovernorates.join(", ")}
          </p>
        </div>
      )}
    </div>
  );
};

export default GovernorateTaskChart;
